import { FC, ReactNode } from 'react'
import styled from 'styled-components'

interface FormulaDisplayProps {
  /** Heading line above the formula, e.g. "Circumferential -- API 579 Annex 2C.5.3.1". */
  label: string
  /** Symbolic formula JSX. Use `<sub>` / `<sup>` for subscripts / superscripts. */
  symbolic: ReactNode
  /**
   * Substituted-values JSX (same structure as `symbolic`, but with numbers in
   * place of symbols). Wrap values in `<Tooltip>` to surface provenance on hover.
   */
  substituted: ReactNode
  /** Final computed value + unit (e.g. `{ value: 3.04, unit: 'mm' }`). */
  result: { value: number; unit: string; precision?: number }
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.75rem 0;
  font-family:
    ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Consolas, monospace;
  font-size: 0.875rem;
`

const Label = styled.div`
  font-family:
    -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  font-size: 0.875rem;
  font-weight: 600;
  color: ${({ theme }) => theme.forecast.activeText};
`

const FormulaRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
  color: ${({ theme }) => theme.forecast.defaultText};
  line-height: 1.5;
`

const FormulaPrefix = styled.span`
  min-width: 2rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
`

const FormulaBody = styled.span`
  white-space: pre-wrap;
`

const Result = styled.div`
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
  font-weight: 600;
  color: ${({ theme }) => theme.forecast.activeText};
`

/**
 * Renders a single formula in three rows: symbolic -> substituted with numbers
 * -> computed result.
 *
 * Used by the Minimum Thickness Analysis step to expose the math behind
 * computed `t_min` and `MAWP` values. The substituted-values row uses a
 * monospace font so symbols align column-wise across formulas. Wrap values
 * inside `substituted` in `<Tooltip>` to expose per-input provenance on
 * hover (see `InputProvenanceRow` for the matching Inputs block).
 */
export const FormulaDisplay: FC<FormulaDisplayProps> = ({
  label,
  symbolic,
  substituted,
  result,
}) => {
  const formattedResult = result.value.toFixed(result.precision ?? 2)

  return (
    <Container>
      <Label>{label}</Label>
      <FormulaRow>
        <FormulaPrefix />
        <FormulaBody>{symbolic}</FormulaBody>
      </FormulaRow>
      <FormulaRow>
        <FormulaPrefix>=</FormulaPrefix>
        <FormulaBody>{substituted}</FormulaBody>
      </FormulaRow>
      <Result>
        <FormulaPrefix>=</FormulaPrefix>
        <span>
          {formattedResult} {result.unit}
        </span>
      </Result>
    </Container>
  )
}
